"use client";

import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useAppData } from "@/store/appStore";
import { getEligibilityCaps } from "@/app/product/_actions/eligibility.actions";
import { formatCurrency } from "@/app/product/utils/currency.utils";
import { GTMEvent, trackEvent } from "@/lib/utils/gtm-events";
import {
	DrawerBackdrop,
	DrawerBody,
	DrawerContent,
	DrawerRoot,
} from "../ui/drawer";

import Image from "next/image";
import { AvailabilityBadge } from "../ui/availability-badge";
import Link from "next/link";
import { useIsLaunchTheme } from "@/theme/ThemeProvider";

// --- Helper: derive company name from employment email ---

function deriveCompanyName(email?: string | null): string {
	if (!email) return "";
	const match = email.match(/@([^.]+)/);
	if (!match?.[1]) return "";
	const raw = match[1];
	return raw
		.split(/[\s._-]+/)
		.filter(Boolean)
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ");
}

// --- Helper utilities ---

function isSubtypeRules(
	rules: ProgramCaps["bookingRules"],
): rules is Record<string, BookingRuleEntry> {
	if (!rules) return false;
	return !("maxPerYear" in rules);
}

function getDeviceAvailability(device: ProgramCaps) {
	if (!device.bookingRules) return { available: 0, total: 0 };
	if (isSubtypeRules(device.bookingRules)) {
		let available = 0;
		let total = 0;
		for (const entry of Object.values(device.bookingRules)) {
			available += entry.activeLeaseLimit ? 0 : 1;
			total += 1;
		}
		return {
			available: device.effectiveAvailable ?? available,
			total: device.effectiveTotal ?? total,
		};
	}
	return {
		available: device.bookingRules.activeLeaseLimit ? 0 : 1,
		total: device.bookingRules.maxPerYear,
	};
}

function getCarAvailability(car: ProgramCaps) {
	if (!car.bookingRules) return { available: 0, total: 0 };
	if (isSubtypeRules(car.bookingRules)) {
		let available = 0;
		let total = 0;
		for (const entry of Object.values(car.bookingRules)) {
			available += entry.activeLeaseLimit ? 0 : 1;
			total += 1;
		}
		return { available, total };
	}
	return {
		available: car.bookingRules.activeLeaseLimit ? 0 : 1,
		total: car.bookingRules.maxPerYear,
	};
}

const SUBTYPE_ICONS: Record<string, string> = {
	mobile: "/new_icons/eligibility/smartphones.svg",
	laptop: "/new_icons/eligibility/laptops.svg",
	tablet: "/new_icons/eligibility/tablets.svg",
};

const SUBTYPE_LABELS: Record<string, string> = {
	mobile: "Mobiles",
	laptop: "Laptops",
	tablet: "Tablets",
};

// --- Popup content (shared between dropdown + drawer) ---

function PopupContent({
	data,
	partnerName,
	onClose,
}: {
	data: EligibilityCapsResponse;
	partnerName: string;
	onClose: () => void;
}) {
	const device = data.device;
	const car = data.car;

	const deviceAvail = device?.isAvailable
		? getDeviceAvailability(device)
		: null;
	const carAvail = car?.isAvailable ? getCarAvailability(car) : null;

	return (
		<div className="flex flex-col gap-4">
			{/* Header */}
			<div className="flex items-start justify-between">
				<div>
					<p className="text-base text-automint-black mt-2 md:mt-0 leading-tight">
						What you can order right now
					</p>
					<p className="text-[11px] md:text-xs text-text-gray ">
						as per {partnerName ? `${partnerName}'s` : "your company's"} policy
						and your declared CTC
					</p>
				</div>
				<button
					onClick={onClose}
					className="p-1 rounded-md hover:bg-gray-100 md:hidden"
				>
					<Image
						src="/new_icons/general/close.svg"
						alt="Close"
						width={30}
						height={30}
					/>
				</button>
			</div>

			{/* Devices section */}
			{device?.isAvailable && deviceAvail && (
				<div className="flex flex-col gap-3 rounded-2xl bg-white p-4">
					<div className="flex items-start justify-between">
						<div className="flex items-start gap-1.5">
							<Image
								src="/new_icons/eligibility/devices-card-icon.svg"
								alt="Devices"
								width={17}
								height={12}
								className="mt-0.5"
							/>
							<div>
								<p className="text-sm text-automint-black leading-none">
									Devices
								</p>
								{(() => {
									const cap =
										device.priceCap?.allowed ??
										(typeof device.ctcPercent?.allowed === "object"
											? device.ctcPercent.allowed.value
											: undefined);
									return typeof cap === "number" ? (
										<p className="text-[11px] text-text-gray-secondary">
											Up to {formatCurrency(cap, false)}
										</p>
									) : null;
								})()}
							</div>
						</div>
						<AvailabilityBadge
							available={deviceAvail.available}
							total={deviceAvail.total}
						/>
					</div>

					{/* Subtype breakdown */}
					{isSubtypeRules(device.bookingRules) && (
						<div className="flex items-center gap-2">
							{Object.entries(device.bookingRules).map(([subtype, rule]) => (
								<div
									key={subtype}
									className="flex flex-col items-center gap-1.5 flex-1 rounded-xl border border-gray-200 bg-transparent py-3"
								>
									<div className="relative">
										<Image
											src={
												SUBTYPE_ICONS[subtype] ??
												"/new_icons/eligibility/smartphones.svg"
											}
											alt={subtype}
											width={26}
											height={26}
										/>
										<span
											className={`absolute -top-1.5 -right-1 w-4 h-4 rounded-full text-[10px] font-medium flex items-center justify-center ${
												rule.activeLeaseLimit
													? "bg-gray-300 text-gray-600"
													: "bg-automint-neon text-automint-black"
											}`}
										>
											{rule.activeLeaseLimit ? 0 : 1}
										</span>
									</div>
									<p
										className={`text-[13px] font-light ${rule.activeLeaseLimit ? "text-muted-gray" : "text-automint-black"}`}
									>
										{SUBTYPE_LABELS[subtype] ?? subtype}
									</p>
								</div>
							))}
						</div>
					)}
				</div>
			)}

			{/* Cars section */}
			{car?.isAvailable && carAvail && (
				<div className="flex items-start justify-between rounded-2xl bg-white p-4">
					<div className="flex items-start gap-1.5">
						<Image
							src="/new_icons/eligibility/cars-card-icon.svg"
							alt="Cars"
							width={23}
							height={23}
							className="-mt-[5px]"
						/>
						<div>
							<p className="text-sm text-automint-black leading-tight">Cars</p>
							{(() => {
								const cap =
									car.priceCap?.allowed ??
									(typeof car.ctcPercent?.allowed === "object"
										? car.ctcPercent.allowed.value
										: undefined);
								return typeof cap === "number" ? (
									<p className="text-[11px] text-text-gray-secondary">
										Up to {formatCurrency(cap, false)}
									</p>
								) : null;
							})()}
						</div>
					</div>
					<AvailabilityBadge available={carAvail.available} />
				</div>
			)}

			{/* CTA */}
			<Link
				href="/product"
				onClick={onClose}
				className="w-full bg-automint-black text-automint-neon text-center py-4 text-sm rounded-xl hover:bg-gray-800 transition-colors block"
			>
				Browse products
			</Link>

			{/* View eligibility details */}
			<Link
				href="/profile#eligibility"
				onClick={onClose}
				className="text-center text-sm text-automint-black underline underline-offset-2"
			>
				View eligibility details
			</Link>
		</div>
	);
}

// --- Main component ---

const EligibilityCapsPopup = ({
	initialData,
}: {
	initialData?: EligibilityCapsResponse | null;
}) => {
	const isLaunch = useIsLaunchTheme();
	const [appData, , setCookielessData, hasHydrated] = useAppData();
	const [isOpen, setIsOpen] = useState(false);
	const [capsData, setCapsData] = useState<EligibilityCapsResponse | null>(
		initialData ?? null,
	);

	// Sync with store
	useEffect(() => {
		if (capsData) {
			setCookielessData({ eligibilityCaps: capsData });
		}
	}, [capsData, setCookielessData]);
	const [loading, setLoading] = useState(false);
	const [isMobile, setIsMobile] = useState(false);
	const [mounted, setMounted] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const [dropdownPos, setDropdownPos] = useState({
		top: 0,
		right: 0,
		arrowRight: 0,
	});

	const [fetchError, setFetchError] = useState(false);
	const [refetchTrigger, setRefetchTrigger] = useState(0);
	// Skip the initial client-side fetch if server already provided data
	const skipInitialFetchRef = useRef(!!initialData);

	// Sync capsData when server provides fresh data (e.g. after router.refresh())
	useEffect(() => {
		if (initialData) {
			setCapsData(initialData);
		}
	}, [initialData]);

	const isLoggedIn = appData?.user && appData?.isUserReady;

	useEffect(() => {
		setMounted(true);
	}, []);

	useEffect(() => {
		if (typeof window === "undefined") return;
		const update = () => setIsMobile(window.innerWidth < 768);
		update();
		window.addEventListener("resize", update);
		return () => window.removeEventListener("resize", update);
	}, []);

	// Invalidate caps after order placement
	useEffect(() => {
		const handler = () => setRefetchTrigger((v) => v + 1);
		window.addEventListener("caps-invalidated", handler);
		return () => window.removeEventListener("caps-invalidated", handler);
	}, []);

	// Fetch caps when user becomes logged in or a refetch is triggered
	useEffect(() => {
		if (!isLoggedIn) return;

		// Skip the first fetch if server-side initial data was provided
		if (skipInitialFetchRef.current) {
			skipInitialFetchRef.current = false;
			return;
		}

		let active = true;
		setLoading(true);
		setFetchError(false);
		// Don't clear existing capsData — keep showing stale data while refetching
		// (prevents the loading spinner from flashing when data is already available)

		getEligibilityCaps(appData?.ctc || undefined)
			.then((result) => {
				if (!active) return;
				if (result.success) {
					setCapsData(result.data);
				} else {
					setFetchError(true);
				}
			})
			.catch(() => {
				if (active) setFetchError(true);
			})
			.finally(() => {
				if (active) setLoading(false);
			});

		return () => {
			active = false;
		};
	}, [isLoggedIn, refetchTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

	const updateDropdownPosition = () => {
		if (containerRef.current) {
			const rect = containerRef.current.getBoundingClientRect();
			const scrollbarWidth =
				window.innerWidth - document.documentElement.clientWidth;
			const rightOffset = window.innerWidth - rect.right - scrollbarWidth;
			setDropdownPos({
				top: rect.bottom + 12,
				right: rightOffset,
				arrowRight: 28,
			});
		}
	};

	const handleToggle = () => {
		if (fetchError && !capsData) {
			setRefetchTrigger((v) => v + 1);
			return;
		}
		if (!isOpen) {
			updateDropdownPosition();
			trackEvent(GTMEvent.ELIGIBILITY_VISITED, { page_path: "homepage" });
		}
		setIsOpen(!isOpen);
	};

	const handleClose = () => setIsOpen(false);

	useEffect(() => {
		if (!mounted || !isLoggedIn) return;
		const expires = new Date(Date.now() + 365 * 86400e3).toUTCString();
		document.cookie = `eligibility_hint_dismissed=1; expires=${expires}; path=/`;
	}, [mounted, isLoggedIn]);

	useEffect(() => {
		if (!mounted || isLoggedIn) return;
		const dismissed = document.cookie
			.split("; ")
			.some((c) => c.startsWith("eligibility_hint_dismissed=1"));
		if (!dismissed) {
			updateDropdownPosition();
			setIsOpen(true);
		}
	}, [mounted, isLoggedIn]);

	useEffect(() => {
		if (!isOpen || isLoggedIn) return;
		const handler = () => updateDropdownPosition();
		window.addEventListener("scroll", handler, { passive: true });
		window.addEventListener("resize", handler);
		return () => {
			window.removeEventListener("scroll", handler);
			window.removeEventListener("resize", handler);
		};
	}, [isOpen, isLoggedIn]);

	useEffect(() => {
		if (!isOpen || isMobile) return;
		const handleClickOutside = (e: MouseEvent) => {
			const target = e.target as Node;
			if (
				containerRef.current &&
				!containerRef.current.contains(target) &&
				(!dropdownRef.current || !dropdownRef.current.contains(target))
			) {
				setIsOpen(false);
			}
		};
		document.addEventListener("click", handleClickOutside);
		return () => {
			document.removeEventListener("click", handleClickOutside);
		};
	}, [isOpen, isMobile]);

	if (!hasHydrated) return null;

	if (!isLoggedIn) {
		const guestPopup =
			isOpen && mounted ? (
				<div
					ref={dropdownRef}
					className={"fixed z-[999999] w-[260px]"}
					style={{
						top: `${dropdownPos.top}px`,
						right: `${dropdownPos.right}px`,
						filter:
							"drop-shadow(0 0 15px rgba(0,0,0,0.03)) drop-shadow(0 10px 26px rgba(0,0,0,0.03)) drop-shadow(0 30px 35px rgba(0,0,0,0.02))",
					}}
				>
					{/* Pointy-tip arrow — curved sides converging at a sharp apex (~90° V at the tip) */}
					<svg
						className="absolute -top-[13px] right-9 pointer-events-none block"
						width="40"
						height="14"
						viewBox="0 0 40 14"
						fill={isLaunch ? "#DDBCC5" : "#F1FB76"}
						xmlns="http://www.w3.org/2000/svg"
					>
						<path d="M0 14 C 14 14, 14 6, 20 0 C 26 6, 26 14, 40 14 Z" />
					</svg>
					<div className={"am-accent-fill relative rounded-[10px]"}>
						<Link
							href="/authenticate/email"
							onClick={() => setIsOpen(false)}
							className="block p-4"
						>
							<p className="text-automint-black font-medium text-sm mb-1 pr-4">
								Check your eligibility
							</p>
							<p className="text-automint-black font-light text-xs leading-snug">
								Login to see what you can order (when and why), as per your
								company&apos;s policy
							</p>
						</Link>
						<button
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								const expires = new Date(
									Date.now() + 365 * 86400e3,
								).toUTCString();
								document.cookie = `eligibility_hint_dismissed=1; expires=${expires}; path=/`;
								setIsOpen(false);
							}}
							className="absolute top-2 right-2 p-1 rounded-md hover:bg-black/10 transition-colors z-10"
							aria-label="Close"
						>
							<Image
								src="/new_icons/general/black-cross.svg"
								alt="Close"
								width={16}
								height={16}
							/>
						</button>
					</div>
				</div>
			) : null;

		return (
			<div ref={containerRef} className="relative">
				<button
					onClick={() => {
						if (!isOpen) updateDropdownPosition();
						setIsOpen(!isOpen);
					}}
					className="flex items-center gap-2 px-2.5 h-[42px] md:gap-4 md:px-3 rounded-[10px] bg-[var(--colors-automint-neutral-800)] hover:bg-neutral-700 transition-colors cursor-pointer"
				>
					<span className="flex items-center gap-1 text-sm">
						<Image
							src="/new_icons/eligibility/devices.svg"
							alt="Devices"
							width={20}
							height={20}
						/>
						<span className="text-automint-neon text-sm">?</span>
					</span>
					<span className="flex items-center gap-1 text-sm">
						<Image
							src="/new_icons/eligibility/cars.svg"
							alt="Cars"
							width={20}
							height={20}
						/>
						<span className="text-automint-neon text-sm">?</span>
					</span>
				</button>
				{mounted && guestPopup && createPortal(guestPopup, document.body)}
			</div>
		);
	}
	// Don't render at all while doing the initial fetch with no data —
	// avoids showing a persistent spinner on mobile first load
	if (!capsData && loading) return null;
	// If caps loaded and no program is available, hide the popup entirely
	if (capsData && !capsData.device?.isAvailable && !capsData.car?.isAvailable)
		return null;

	// Compute counts from cached data
	const deviceCount =
		capsData?.device?.isAvailable && capsData.device.bookingRules
			? isSubtypeRules(capsData.device.bookingRules)
				? (capsData.device.effectiveAvailable ??
					Object.values(capsData.device.bookingRules).reduce(
						(sum, r) => sum + (r.activeLeaseLimit ? 0 : 1),
						0,
					))
				: capsData.device.bookingRules.activeLeaseLimit
					? 0
					: 1
			: null;

	const carCount =
		capsData?.car?.isAvailable && capsData.car.bookingRules
			? isSubtypeRules(capsData.car.bookingRules)
				? Object.values(capsData.car.bookingRules).reduce(
						(sum, r) => sum + (r.activeLeaseLimit ? 0 : 1),
						0,
					)
				: capsData.car.bookingRules.activeLeaseLimit
					? 0
					: 1
			: null;

	const partnerName = deriveCompanyName(appData?.user?.employment?.email) || "";

	// --- Trigger button ---
	const trigger = (
		<button
			onClick={handleToggle}
			className="flex items-center gap-2 px-2.5 h-[42px] md:gap-4 md:px-3 rounded-[10px] bg-[var(--colors-automint-neutral-800)] hover:bg-neutral-700 transition-colors cursor-pointer"
		>
			{deviceCount !== null && (
				<span className="flex items-center gap-1 text-automint-neon text-sm">
					<Image
						src="/new_icons/eligibility/devices.svg"
						alt="Devices"
						width={20}
						height={20}
					/>
					{deviceCount}
				</span>
			)}
			{carCount !== null && (
				<span className="flex items-center gap-1 text-automint-neon text-sm">
					<Image
						src="/new_icons/eligibility/cars.svg"
						alt="Cars"
						width={20}
						height={20}
					/>
					{carCount}
				</span>
			)}
			{deviceCount === null &&
				carCount === null &&
				(loading ? (
					<div className="w-4 h-4 border-2 border-gray-500 border-t-automint-neon rounded-full animate-spin" />
				) : fetchError ? (
					<svg
						width="14"
						height="14"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2.5"
						className="text-gray-400"
					>
						<path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
						<path d="M21 3v5h-5" />
						<path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
						<path d="M8 16H3v5" />
					</svg>
				) : null)}
		</button>
	);

	// --- Popup body ---
	const popupBody = capsData ? (
		<PopupContent
			data={capsData}
			partnerName={partnerName}
			onClose={handleClose}
		/>
	) : loading ? (
		<div className="flex items-center justify-center py-12">
			<div className="w-6 h-6 border-2 border-gray-300 border-t-gray-800 rounded-full animate-spin" />
		</div>
	) : null;

	// Desktop: anchored dropdown with arrow
	const desktopDropdown =
		isOpen && !isMobile && mounted ? (
			<div
				ref={dropdownRef}
				className={"fixed z-[999999] w-[380px] rounded-[20px] p-5"}
				style={{
					top: `${dropdownPos.top}px`,
					right: `${dropdownPos.right}px`,
					backgroundColor: "var(--colors-automint-neutral-100)",
					boxShadow:
						"0 300px 91px 0 rgba(0,0,0,0.00), 0 150px 84px 0 rgba(0,0,0,0.00), 0 100px 70px 0 rgba(0,0,0,0.02), 0 30px 52px 0 rgba(0,0,0,0.03), 0 0 29px 0 rgba(0,0,0,0.03)",
				}}
			>
				{/* Arrow pointing up */}
				<div
					className="absolute -top-2 w-4 h-4 rotate-45"
					style={{
						right: `${dropdownPos.arrowRight}px`,
						backgroundColor: "var(--colors-automint-neutral-100)",
						boxShadow: "-2px -2px 4px rgba(0,0,0,0.02)",
					}}
				/>
				{popupBody}
			</div>
		) : null;

	// Mobile: bottom drawer
	if (isMobile) {
		return (
			<div ref={containerRef}>
				{trigger}
				<DrawerRoot
					placement="bottom"
					open={isOpen}
					onOpenChange={(details) => setIsOpen(details.open)}
				>
					<DrawerBackdrop />
					<DrawerContent
						borderTopRadius="20px"
						background="var(--colors-automint-neutral-100)"
					>
						<DrawerBody padding="5" paddingTop="4">
							{popupBody}
						</DrawerBody>
					</DrawerContent>
				</DrawerRoot>
			</div>
		);
	}

	return (
		<div ref={containerRef} className="relative">
			{trigger}
			{mounted &&
				desktopDropdown &&
				createPortal(desktopDropdown, document.body)}
		</div>
	);
};

export default EligibilityCapsPopup;
